// src/components/common/ClientTypeahead.jsx
//
// Free-text client field that OFFERS Autotask matches without requiring one.
//
// ── WHY THIS IS ITS OWN FILE ───────────────────────────────────────────────
// It lived as a module-local function inside MspOsApp.jsx, which meant nothing
// outside that file could use it — so when Proposals needed the same control,
// the choice was "export it" or "write a second one". A second one already
// existed: OnboardingPanel.jsx had its own companyQuery/matches filter, built
// independently. Two implementations of "filter companies by name" is how they
// drift, and the quote-side work added a third behaviour (the matched-company
// callback) that only one of them had.
//
// Moved here VERBATIM — same props, same behaviour, same markup and class
// names. The `.typeahead*` CSS still lives in MspOsApp.jsx's global style block
// and is not duplicated; this component only ever renders inside that app.
import { useState, useEffect, useRef } from "react";

/**
 * @param value        the current text — the caller owns it
 * @param onChange     (name, matchedCompany) — see the warning below
 * @param companies    Autotask companies, each { id, companyName, _tier? }
 * @param placeholder  input placeholder
 * @param inputStyle   optional style for the input, so a caller can match the
 *                     row it sits in (the quote builder's fields are 11px)
 *
 * ⚠ onChange HAS AN OPTIONAL SECOND ARGUMENT: onChange(name, matchedCompany).
 * `matchedCompany` is the company object ONLY when the user explicitly picked
 * it from the dropdown, and null on every keystroke. Callers that want a real
 * Autotask link use it to tell "picked a known company" from "typed a name" —
 * and, just as importantly, to CLEAR a link when the name is typed over.
 * The first argument is unchanged, so existing callers that ignore the second
 * keep working exactly as before.
 */
export default function ClientTypeahead({ value, onChange, companies, placeholder="Search client...", inputStyle }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value||"");
  const [active, setActive] = useState(-1);
  const ref = useRef(null);

  // Sync query when value changes externally
  useEffect(()=>{ setQuery(value||""); },[value]);

  // Close on outside click
  useEffect(()=>{
    const handler=(e)=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[]);

  /* `companies` is defaulted at the destructure rather than assumed: this is
     now imported by panels that render before the Autotask cache has loaded,
     where the old single caller always had a [] in hand. */
  const filtered = query.trim().length===0 ? [] :
    (companies||[])
      .filter(c=>(c.companyName||"").toLowerCase().includes(query.toLowerCase()))
      .slice(0,10);

  // Only reached by an EXPLICIT pick — click or Enter on a highlighted row —
  // so this is the one path that may hand a caller a real company to link to.
  const select=(name,matched)=>{
    setQuery(name);
    onChange(name,matched||null);
    setOpen(false);
    setActive(-1);
  };

  const handleKey=(e)=>{
    if(!open||filtered.length===0) return;
    if(e.key==="ArrowDown"){ e.preventDefault(); setActive(a=>Math.min(a+1,filtered.length-1)); }
    if(e.key==="ArrowUp"){   e.preventDefault(); setActive(a=>Math.max(a-1,-1)); }
    if(e.key==="Enter"&&active>=0){ e.preventDefault(); select(filtered[active].companyName,filtered[active]); }
    if(e.key==="Escape"){ setOpen(false); }
  };

  return(
    <div className="typeahead" ref={ref}>
      <input className="inp typeahead-input"
        placeholder={placeholder}
        style={inputStyle}
        value={query}
        /* Typing passes null: whatever was linked before is no longer what the
           field says, and a caller holding an id must be able to drop it. */
        onChange={e=>{ setQuery(e.target.value); onChange(e.target.value,null); setOpen(true); setActive(-1); }}
        onFocus={()=>{ if(query.trim()) setOpen(true); }}
        onKeyDown={handleKey}
        autoComplete="off"
      />
      {open&&filtered.length>0&&(
        <div className="typeahead-dropdown">
          {filtered.map((c,i)=>(
            <div key={c.id} className={`typeahead-item ${i===active?"ti-active":""}`}
              onMouseDown={e=>{ e.preventDefault(); select(c.companyName,c); }}>
              <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.companyName}</span>
              <span className={`typeahead-tier tt-${c._tier||"Adhoc"}`}>{c._tier||"Adhoc"}</span>
            </div>
          ))}
        </div>
      )}
      {open&&query.trim()&&filtered.length===0&&(
        <div className="typeahead-dropdown">
          <div className="typeahead-item" style={{color:"var(--text3)",fontStyle:"italic"}}>No matching clients</div>
        </div>
      )}
    </div>
  );
}
